import { useEffect, useMemo, useRef } from 'react';
import { useField } from 'formik';
import { Space } from 'antd';
import FileTextIconOutlined from 'components/icons/FileTextIconOutlined';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import FormSelect, { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import { ICS2MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { ics2EnsNameLabels } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { ContainerStatuses, getContainerStatusLabel } from 'utils/containerStatus';
import { TransportTypeLabels, TransportTypes } from 'utils/transportTypes';
import { useBrokerUploadContext } from '../BrokerUploadProvider';
import { BrokerUploadSteps } from '../types';

const stepKey = BrokerUploadSteps.FORM_TYPE;
const transportTypeFieldName = 'formType.entryTransportType';
const formTypeFieldName = 'formType.formType';

// ICS2 entry covers road and maritime movements only.
const entryTransportTypeOptions: SelectOption[] = [TransportTypes.ROAD, TransportTypes.MARITIME].map((id) => ({
    id,
    value: TransportTypeLabels[id],
}));

const EnsFormStep = () => {
    const [transportTypeField, transportTypeMeta, transportTypeHelper] = useField(transportTypeFieldName);
    const [formTypeField, formTypeMeta, formTypeHelper] = useField(formTypeFieldName);
    const { isActive, isComplete, isIdle, resetStep, goNext, currentStep } = useMultiStepFormContext<any>();
    const { selectedManifest } = useBrokerUploadContext();

    const containerIndicator = selectedManifest?.declarationsData?.containerIndicator;
    const movementType = containerIndicator == null ? undefined : getContainerStatusLabel(Number(containerIndicator));
    const isAccompanied = movementType != null && Number(containerIndicator) === ContainerStatuses.CONTAINERIZED;

    const isDerivedFromManifest = !!movementType;
    const hasTransportType = transportTypeField.value != null && transportTypeField.value !== '';
    const isMaritime = Number(transportTypeField.value) === TransportTypes.MARITIME;
    const previousTransportTypeRef = useRef(transportTypeField.value);

    const isFormTypeDisabled = (key: string) => {
        if (!hasTransportType) return false;
        return key === ICS2MessageTypes.F11 ? !isMaritime : isMaritime;
    };

    const formTypeOptions: SelectOption[] = useMemo(
        () =>
            ics2EnsNameLabels.map(({ key, value }) => ({
                id: key,
                value,
                disabled: isFormTypeDisabled(key),
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [hasTransportType, isMaritime]
    );

    useEffect(() => {
        if (!movementType || hasTransportType) return;

        transportTypeHelper.setValue(isAccompanied ? TransportTypes.ROAD : TransportTypes.MARITIME);
        formTypeHelper.setValue(isAccompanied ? ICS2MessageTypes.F50 : ICS2MessageTypes.F11);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [movementType, isAccompanied, hasTransportType]);

    // Both answers come from the manifest and the inputs are disabled, so there is nothing to fill
    // in here. Auto-progression only reacts to value changes, and those are written while the
    // manifest step is still the current one, so the wizard has to be moved on explicitly.
    useEffect(() => {
        if (!isDerivedFromManifest || !isActive(stepKey)) return;
        if (!hasTransportType || !formTypeField.value) return;

        goNext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDerivedFromManifest, hasTransportType, formTypeField.value, currentStep]);

    // Changing the transport type afterwards resets the form, leaving the user to pick
    // from the options that transport type still allows.
    useEffect(() => {
        const previousTransportType = previousTransportTypeRef.current;
        previousTransportTypeRef.current = transportTypeField.value;

        const hadTransportType = previousTransportType != null && previousTransportType !== '';
        if (!hadTransportType || previousTransportType === transportTypeField.value) return;

        formTypeHelper.setValue(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transportTypeField.value]);

    const sectionTitle = 'Form';
    const subtitle = movementType ? `Form auto-selected based on movement type: ${movementType}` : undefined;
    const readonlyValue = useMemo(
        () => formTypeOptions.find((option) => option.id === formTypeField.value)?.value ?? formTypeField.value,
        [formTypeField.value, formTypeOptions]
    );

    if (isIdle(stepKey)) {
        return <FormSectionHeader title={sectionTitle} icon={<FileTextIconOutlined />} />;
    }

    if (isComplete(stepKey)) {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <FormSectionHeader title={sectionTitle} icon={<FileTextIconOutlined />} />
                <ReadonlyFormItem
                    disabled={isDerivedFromManifest}
                    value={readonlyValue}
                    onReset={() => resetStep(stepKey, 'formType')}
                />
            </div>
        );
    }

    if (isActive(stepKey)) {
        return (
            <Space style={{ width: '100%' }} direction="vertical">
                <FormSectionHeader title={sectionTitle} icon={<FileTextIconOutlined />} subtitle={subtitle} />

                <FormSelect
                    required
                    hideKeys
                    disabled={isDerivedFromManifest}
                    label="Entry Transport Type"
                    placeholder="Select Entry Transport Type"
                    selectOptions={entryTransportTypeOptions}
                    fieldProps={transportTypeField}
                    fieldMeta={transportTypeMeta}
                    fieldHelper={transportTypeHelper}
                />
                <FormSelect
                    required
                    hideKeys
                    disabled={isDerivedFromManifest}
                    label="Form"
                    placeholder="Select Form"
                    selectOptions={formTypeOptions}
                    fieldProps={formTypeField}
                    fieldMeta={formTypeMeta}
                    fieldHelper={formTypeHelper}
                />
            </Space>
        );
    }

    return null;
};

export default EnsFormStep;
